/**
 * Czyste funkcje okna „Nowa faktura → Z rezerwacji" (components/invoices/ReservationPicker.js).
 * Bez Reacta i bez sieci, zeby dalo sie je przetestowac bez przegladarki.
 */
import { localeTag, toDate } from './format';
import { calculateFinalPrice } from '../pages/calendar/utils';

export const PAGE_SIZE = 50;
export const PERIODS = ['all', 'thisMonth', 'lastMonth', 'last3Months'];

const pad = (n) => String(n).padStart(2, '0');

/** Data lokalna jako `RRRR-MM-DD` — bez `toISOString`, ktore przesuwa dzien o strefe czasowa. */
export const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * Zakres dat dla okresu z listy. Pelne miesiace kalendarzowe, bo tak rozlicza sie faktury;
 * „ostatnie 3 miesiace" = biezacy i dwa poprzednie. `new Date(r, m, 0)` to ostatni dzien
 * miesiaca `m - 1`, a ujemny miesiac przechodzi przez granice roku sam.
 */
export function periodRange(period, today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth();
  const months = (first, last) => ({
    from: toYmd(new Date(y, first, 1)),
    to: toYmd(new Date(y, last + 1, 0)),
  });
  switch (period) {
    case 'thisMonth': return months(m, m);
    case 'lastMonth': return months(m - 1, m - 1);
    case 'last3Months': return months(m - 2, m);
    default: return { from: null, to: null };
  }
}

export function candidatesQueryString({ q = '', scope = 'open', period = 'all', offset = 0, limit = PAGE_SIZE, today }) {
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  params.set('scope', scope);
  const { from, to } = periodRange(period, today);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return params.toString();
}

/**
 * Grupy kolejnych wierszy z tego samego miesiaca rozpoczecia. Lista przychodzi juz posortowana
 * od najnowszych, wiec wystarczy porownanie z ostatnia grupa — kolejnosc zostaje nietknieta.
 */
export function groupByMonth(rows, locale) {
  const fmt = new Intl.DateTimeFormat(localeTag(locale), { month: 'long', year: 'numeric' });
  const groups = [];
  for (const row of rows) {
    const d = toDate(row.start_date);
    const key = d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}` : 'none';
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = { key, label: d ? fmt.format(d) : '', items: [] };
      groups.push(group);
    }
    group.items.push(row);
  }
  return groups;
}

/** Dzien miesiaca i skrot dnia tygodnia do kartki z data przy wierszu. */
export function dayStub(value, locale) {
  const d = toDate(value);
  if (!d) return { day: '', weekday: '' };
  return {
    day: String(d.getDate()),
    weekday: new Intl.DateTimeFormat(localeTag(locale), { weekday: 'short' }).format(d),
  };
}

/** `one` / `few` / `many` — polski ma trzy formy („1 rezerwacja, 3 rezerwacje, 5 rezerwacji"). */
export function pluralCategory(n, locale) {
  try {
    const category = new Intl.PluralRules(localeTag(locale)).select(n);
    return category === 'one' || category === 'few' ? category : 'many';
  } catch (_) {
    return n === 1 ? 'one' : 'many';
  }
}

/**
 * Co pokazac przy wierszu. Faktura koncowa wygrywa (z jej numerem — to po nim uzytkownik
 * rozpoznaje, ze juz ja wystawil), potem anulowanie, na koncu sama proforma.
 */
export function documentBadge(row) {
  const docs = Array.isArray(row.documents) ? row.documents : [];
  const final = docs.find((d) => d.doc_type === 'VAT' || d.doc_type === 'SIMPLIFIED');
  if (final) return { kind: 'invoice', number: final.number };
  if (row.canceled) return { kind: 'canceled' };
  const proforma = docs.find((d) => d.doc_type === 'PROFORMA');
  if (proforma) return { kind: 'proforma', number: proforma.number };
  return null;
}

/**
 * Kwota przy wierszu: zapisana kwota rezerwacji PO rabacie, liczona ta sama regula co lista
 * kalendarza (`calculateFinalPrice`), bo `total_price` jest zawsze kwota PRZED rabatem.
 * `null` = kwoty nie ma: brak platnosci albo zapisane 0 zl, KAZDE, takze przy rabacie. Ta sama
 * regula co serwer (Login/backend/lib/wartoscRezerwacji.js#kwotaZapisana), z ktorej liczy sie
 * faktura z tej rezerwacji: przy braku kwoty bierze cennik, ktorego okno nie zna, wiec wiersz
 * pokazuje kreske zamiast „0,00 zl”, ktore nie zgadzaloby sie z faktura. Darmowa rezerwacja ma
 * zapisana kwote i rabat 100%, wiec dalej pokazuje 0. Wartosci uszkodzone starszymi zapisami
 * czytamy jak serwer: nieliczbowa albo ujemna kwota to brak kwoty, a ujemny albo nieliczbowy
 * rabat to brak rabatu.
 */
export function kwotaPoRabacie(row) {
  // Rezerwacja ze znacznikiem z migracji 052: faktura liczy ją z cennika, więc zapisana kwota
  // minus rabat nie jest kwotą faktury. Brak kwoty zamiast liczby, która się nie zgodzi.
  if (row && row.amount_from_price_list) return null;
  const surowa = row ? row.total_price : null;
  if (surowa === null || surowa === undefined || surowa === '') return null;
  const przedRabatem = Number(surowa);
  if (!Number.isFinite(przedRabatem) || przedRabatem <= 0) return null;
  const rabatLiczba = Number(row.discount);
  const rabat = Number.isFinite(rabatLiczba) && rabatLiczba > 0 ? rabatLiczba : 0;
  return calculateFinalPrice({ total_price: przedRabatem, discount: rabat, discount_unit: row.discount_unit });
}
